import type { SongRequest } from '../services/requests';
import type { PlayerMismatch } from './playerMismatch';
import type { PlayerPositionReport } from './nowPlayingCountdown';

export type PreviousFate = 'played' | 'queued';
export type RecoveryEntry = 'fix' | 'choose' | 'letItPlay';

export interface DetectedTrack {
  title: string;
  artist: string;
  durationMs: number | null;
}

export type RecoveryOutcome =
  | { kind: 'pickFromQueue'; requestId: string; previousTo: PreviousFate; flagPicked: boolean }
  | { kind: 'letItPlay'; track: DetectedTrack; previousTo: PreviousFate }
  | { kind: 'clear'; previousTo: PreviousFate };

// Flags are a Bridge fact: only when Bridge is running here and reading the event's player
export function canFlagPick({ level, playerMatchesMode }: { level: 'bridge' | 'checklist'; playerMatchesMode: boolean }): boolean {
  return level === 'bridge' && playerMatchesMode;
}

export function detectedTrackFor({
  level,
  mismatch,
  position,
}: {
  level: 'bridge' | 'checklist';
  mismatch: PlayerMismatch | null;
  position: PlayerPositionReport | null;
}): DetectedTrack | null {
  if (level !== 'bridge') return null;
  const durationMs = position?.durationMs && position.durationMs > 0 ? position.durationMs : null;
  if (mismatch?.noAdvance) return { title: mismatch.title, artist: mismatch.artist, durationMs };
  if (position?.title) return { title: position.title, artist: position.artist ?? '', durationMs };
  return null;
}

// A Let it play song did play; it can't go back to a queue it was never in
export function previousFateOptions(nowPlaying: Pick<SongRequest, 'unplannedPlayAt'> | null): PreviousFate[] {
  if (!nowPlaying) return [];
  return nowPlaying.unplannedPlayAt ? ['played'] : ['played', 'queued'];
}

// Bridge saw the player move on, so the prompt's paths default to Played; Fix makes the DJ choose
export function defaultPreviousFate(entry: RecoveryEntry): PreviousFate | null {
  return entry === 'fix' ? null : 'played';
}
